<template>
    <div class="messages-reports col-12 align-self-center mt-4" ref="scroller" @scroll="updateScrollCue">
        <div class="report-kpis">
            <button type="button" class="report-kpi" @click="scrollToSection('messagesSection')">
                <span class="report-kpi-value"> {{ formatTotals(totalMessages) }} </span>
                <span class="report-kpi-label"> {{ $t('number-sms-messages') }} </span>
            </button>
            <button type="button" class="report-kpi" @click="scrollToSection('contactsSection')">
                <span class="report-kpi-value"> {{ formatTotals(totalContacts) }} </span>
                <span class="report-kpi-label"> {{ $t('number-contacts') }} </span>
            </button>
            <button type="button" class="report-kpi" v-if="topCountry" @click="scrollToSection('countrySection')">
                <span class="report-kpi-value"> {{ topCountry }} </span>
                <span class="report-kpi-label"> {{ $t('top-country') }} </span>
            </button>
        </div>

        <div ref="messagesSection">
        <p class="text-left menu-title text-black fs-14"> {{ $t('number-sms-messages') }} </p>
        <div class="d-flex justify-content-between">
            <div class="d-flex">
                <p class="fs-10 d-flex" :class="{ 'mr-4': ((index + 1) < messageLabels.length) }" v-for="(label, index) in messageLabels" :key="label.key">
                    <span class="fs-14 mr-2 tagged" :style="{'background-color': label.background , color: label.color}"> {{ formatTotals(label.total) }} </span>
                    <span class="align-self-center text-dark-brown"> {{ label.name }} </span>
                </p>
            </div>
            <div>
                <p class="fs-10 d-flex" v-if="reportData && reportData['sms-messages']">
                    <span class="fs-14 mr-2 tagged" :style="{'background-color': '#333333', 'color': '#ffff'}"> {{ formatTotals(reportData['sms-messages'].attributes.total) }} </span>
                    <span class="align-self-center text-dark-brown"> {{ $t('total-messages') }} </span>
                </p>
            </div>
        </div>
        <div class="messages-reports-chart">
            <Bar
                :chart-options="chartOptions"
                :chart-data="chartSmsMessages"
                class="h-100"
            />
        </div>
        </div>
        <div class="pt-5" ref="contactsSection">
            <div class="d-flex justify-content-between">
                <p class="text-left menu-title text-black fs-14"> {{ $t('number-contacts') }}</p>
                <div class="d-flex">
                    <div class="fs-10 d-flex justify-content-center align-items-center" :class="{ 'mr-4': ((index + 1) < messageContactsLabels.length) }" v-for="(label, index) in messageContactsLabels" :key="label.key">
                        <span class="fs-14 mr-2 tagged" :style="{'background-color': label.background , color: label.color}"> {{ formatTotals(label.total) }} </span>
                        <div v-if="label.name === 'CocoApp'">
                            <img src="@/core/assets/images/cocoApp.png" alt="coco"/>
                            <span class="align-self-center"> {{ label.name }} </span>
                        </div>
                        <div v-else-if="label.name === 'facebook'">
                            <img src="@/core/assets/images/facebook.svg" alt="Facebook icon"/>
                            <span class="align-self-center"> {{ label.name }} </span>
                        </div>
                        <div v-else>
                            <span class="align-self-center"> {{ label.name }} </span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="messages-reports-chart">
                <Bar
                    :chart-options="chartContactOptions"
                    :chart-data="chartContactsData"
                    class="h-100"
                />
            </div>
        </div>

        <div v-if="chartSmsMessagesByCountry.labels.length" class="pt-5" ref="countrySection">
            <div class="d-flex justify-content-between">
                <p class="text-left menu-title text-black fs-14"> {{ $t('sms-by-country') }} </p>
            </div>
                <div class="messages-reports-pie">
                    <Pie
                        :chart-options="chartMessagesByCountryOptions"
                        :chart-data="chartSmsMessagesByCountry"
                        class="pie-chart-reports"
                    />
            </div>
        </div>

        <div class="scroll-cue-wrapper">
            <button type="button" class="scroll-cue" :class="{ visible: hasMoreBelow }" :aria-label="$t('scroll-for-more')" @click="scrollDown">
                <span> {{ $t('scroll-for-more') }} </span>
                <font-awesome-icon :icon="['fas', 'chevron-down']" />
            </button>
        </div>
    </div>
</template>
<script>

import moment  from 'moment';
import { Bar, Pie } from 'vue-chartjs/legacy';

import {
    Chart as ChartJS,
    Title,
    Tooltip,
    BarElement,
    PieController,
    CategoryScale,
    ArcElement,
    LinearScale
} from 'chart.js';

import i18nIsoCountries from 'i18n-iso-countries';

import { getPastelColorFromString } from '@/helpers/Color';

const PIE_BORDER_WIDTH = 2;
const PIE_HOVER_OFFSET = 5;

ChartJS.register(Title, Tooltip, BarElement, PieController, CategoryScale, ArcElement, LinearScale);

export default {
    components: {
        Bar,
        Pie
    },

    props: ['reportData'],

    data () {
        const DAILY_REPORT   = 'daily';
        const WEEKLY_REPORT  = 'weekly';
        const MONTHLY_REPORT = 'monthly';

        return {
            hasMoreBelow: false,
            DAILY_REPORT,
            WEEKLY_REPORT,
            MONTHLY_REPORT,
            chartOptions: {
                responsive:          true,
                maintainAspectRatio: false,
                scales:              {
                    x: { ticks: { color: '#462f25' } },
                    y: {
                        suggestedMax: 5,
                        ticks:        {
                            stepSize: 1,
                            color:    '#462f25'
                        }
                    }
                }
            },
            chartContactOptions: {
                responsive:          true,
                maintainAspectRatio: false,
                scales:              {
                    x: { ticks: { color: '#462f25' } },
                    y: {
                        suggestedMax: 5,
                        ticks:        {
                            stepSize: 1,
                            color:    '#462f25'
                        }
                    }
                }
            },
            chartMessagesByCountryOptions: {
                responsive:          true,
                maintainAspectRatio: false,
                layout:              { padding: 10 },
                plugins:             {
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
                                const value = context.raw;
                                // eslint-disable-next-line no-magic-numbers
                                const percentage = ((value / total) * 100).toFixed(1);

                                const lines = [`${context.label}: ${percentage}%`];

                                // Display the number of messages under the percentage
                                const count = context.dataset.messageCounts?.[context.dataIndex];
                                if (count !== undefined) {
                                    lines.push(`${count} ${this.$i18n.t('messages').toLowerCase()}`);
                                }

                                return lines;
                            }
                        }
                    }
                }
            },
            messageLabels: [
                {
                    key:        'message_received',
                    name:       this.$i18n.t('messages-received'),
                    background: '#feaab0',
                    color:      '#462f25',
                    totalKey:   'total_received',
                    total:      0
                },
                {
                    key:        'message_sent',
                    name:       this.$i18n.t('messages-sent'),
                    background: '#149178',
                    color:      '#ffffff',
                    totalKey:   'total_sent',
                    total:      0
                }
            ],
            messageContactsLabels: [
                {
                    attributeKey: 'core-contacts',
                    key:          'contact_created_core',
                    name:         this.$i18n.t('Cocoapp'),
                    background:   '#ffd9e8',
                    color:        '#000000',
                    totalKey:     'total',
                    total:        0
                },
                {
                    attributeKey: 'sms-contacts',
                    key:          'contact_created_sms',
                    name:         this.$i18n.t('sms'),
                    background:   '#ea2f45',
                    color:        '#ffffff',
                    totalKey:     'total',
                    total:        0
                }
            ]
        };
    },
    mounted () {
        window.addEventListener('resize', this.updateScrollCue);
        this.$nextTick(this.updateScrollCue);
    },

    updated () {
        this.updateScrollCue();
    },

    beforeDestroy () {
        window.removeEventListener('resize', this.updateScrollCue);
    },

    computed: {
        totalMessages () {
            return this.reportData['sms-messages'].attributes.total;
        },

        totalContacts () {
            return this.messageContactsLabels.reduce((sum, label) => sum + this.reportData.contacts.attributes[label.attributeKey].total, 0);
        },

        topCountry () {
            const { labels, datasets } = this.chartSmsMessagesByCountry;

            if (!labels.length) {
                return '';
            }

            const data = datasets[0].data;

            return labels[data.indexOf(Math.max(...data))];
        },

        chartSmsMessages () {
            const smsMessages = this.reportData['sms-messages'];

            const labels   = [];
            const datasets = [];

            // Init datasets
            this.messageLabels.forEach(element => {
                element.total = smsMessages.attributes[element.totalKey];
                datasets.push({
                    label:           element.name,
                    backgroundColor: element.background,
                    data:            []
                });
            });

            const dates  = Object.keys(smsMessages.attributes.messages);
            const data   = Object.values(smsMessages.attributes.messages);

            data.forEach((element, key) => {
                // Get the formatted dates
                let formattedDate = '';
                switch (smsMessages.attributes.report_type) {
                    case this.DAILY_REPORT:
                        formattedDate = moment(dates[key]).format('ddd M/D');
                        break;
                    case this.WEEKLY_REPORT:
                        formattedDate = `${moment(dates[key]).format('M/D')} - ${moment(moment(dates[key]).clone()
                            .endOf('week')).format('M/D')}`;
                        break;

                    default:
                        // Monthly report
                        formattedDate = moment(dates[key]).format('MMM Y');
                        break;
                }

                labels.push(formattedDate);
                this.messageLabels.forEach((label, index) => {
                    datasets[index].data.push(element[label.key]);
                });
            });

            return {
                labels,
                datasets
            };
        },

        chartContactsData () {
            const contactsReport = this.reportData.contacts;

            const labels = [];
            const datasets = [];

            this.messageContactsLabels.forEach(label => {
                const source = contactsReport.attributes[label.attributeKey];

                datasets.push({
                    label:           label.name,
                    backgroundColor: label.background,
                    data:            []
                });

                label.total = source.total;
            });

            const firstKey = this.messageContactsLabels[0].attributeKey;
            const contactsObj = contactsReport.attributes[firstKey].contacts;

            const dates = Object.keys(contactsObj);

            dates.forEach(date => {
                let formattedDate = '';

                switch (contactsReport.attributes[firstKey].report_type) {
                    case this.DAILY_REPORT:
                        formattedDate = moment(date).format('ddd M/D');
                        break;

                    case this.WEEKLY_REPORT:
                        formattedDate = `${moment(date).format('M/D')} - ${moment(date).clone()
                            .endOf('week')
                            .format('M/D')}`;
                        break;

                    default:
                        formattedDate = moment(date).format('MMM Y');
                        break;
                }

                labels.push(formattedDate);

                this.messageContactsLabels.forEach((label, index) => {
                    const source = contactsReport.attributes[label.attributeKey];
                    const valueObj = source.contacts[date];

                    datasets[index].data.push(
                        valueObj ? valueObj[label.key] : 0
                    );
                });
            });

            return {
                labels,
                datasets
            };
        },

        chartSmsMessagesByCountry () {
            const smsMessagesByCountry = this.reportData['sms-messages-by-country'];

            const countryCodes = Object.keys(smsMessagesByCountry.attributes.total);

            const labels = countryCodes.map(code => i18nIsoCountries.getName(code, this.$i18n.locale)
            );

            const data = Object.values(smsMessagesByCountry.attributes.total);

            // Same key order as the percentages, so indexes match
            const totalMessages = smsMessagesByCountry.attributes.total_messages;
            const messageCounts = totalMessages ? countryCodes.map(code => totalMessages[code]) : [];

            const preCalculatedColors = [
                '#0D594A',
                '#149178',
                '#BDFFDE',
                '#FFDEEB',
                '#FEAAB0',
                '#F86463',
                '#FFF0AF'
            ];

            const backgroundColor = countryCodes.map((code, index) => {
                // eslint-disable-next-line no-magic-numbers
                if (index <= 6) {
                    return preCalculatedColors[index];
                }

                return getPastelColorFromString(code);
            });

            const hasSeveralCountries = data.length > 1;

            return {
                labels,
                datasets: [
                    {
                        data,
                        messageCounts,
                        backgroundColor,

                        /*
                         * Single country: no dividing line, and no hover offset
                         */
                        borderWidth:      hasSeveralCountries ? PIE_BORDER_WIDTH : 0,
                        hoverBorderWidth: hasSeveralCountries ? PIE_BORDER_WIDTH : 0,
                        hoverOffset:      hasSeveralCountries ? PIE_HOVER_OFFSET : 0
                    }
                ]
            };
        }

    },

    methods: {
        /**
         * Show the scroll cue while there is content below the visible area
         */
        updateScrollCue () {
            const el = this.$refs.scroller;

            if (!el) {
                return;
            }

            // eslint-disable-next-line no-magic-numbers
            this.hasMoreBelow = el.scrollHeight - el.scrollTop - el.clientHeight > 40;
        },

        /**
         * Scroll down by most of the visible height
         */
        scrollDown () {
            const el = this.$refs.scroller;

            // eslint-disable-next-line no-magic-numbers
            el.scrollBy({ top: el.clientHeight * 0.8, behavior: 'smooth' });
        },

        /**
         * Scroll to one of the report sections
         */
        scrollToSection (ref) {
            this.$refs[ref]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },

        /**
         * Format the totals numbers with spaces
         */
        formatTotals (total) {
            return new Intl.NumberFormat(this.$i18n.locale, { useGrouping: true }).format(total);
        }
    }
};
</script>
